import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { DEFAULT_ITEMS_PER_PAGE, DEFAULT_PAGE_NUMBER } from '@components/avis-google-details/config';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button';
import { ReviewLoadingMode } from '@shared/components/review-list/models';
import { StarRatingDisplayComponent } from '@shared/components/star-rating-display';
import { SvgComponent } from '@shared/components/svg';
import { ViewportObserverDirective } from '@shared/directives';
import { TranslatePluralPipe } from '@shared/pipe';
import { LogsService } from '@shared/services/logs';
import { ReviewsService } from '@shared/services/reviews';
import { CustomerReview } from '@shared/services/reviews/models';
import { UrlParamsService } from '@shared/services/url-params';
import { take } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-review-list',
  imports: [
    SvgComponent,
    TranslatePipe,
    TranslatePluralPipe,
    StarRatingDisplayComponent,
    DatePipe,
    ViewportObserverDirective,
    ButtonComponent,
  ],
  templateUrl: './review-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewListComponent {
  readonly loadMoreMode = input<ReviewLoadingMode>('none');
  readonly storeUuid = input<string>();

  readonly reviews = signal<CustomerReview[]>([]);
  private readonly urlParamsService = inject(UrlParamsService);
  readonly selectedRatingFilter = computed(() => Number(this.urlParamsService.param('review_note')() ?? undefined));
  private readonly currentPageFromUrl = computed(() =>
    Number(this.urlParamsService.param('review_page')() ?? DEFAULT_PAGE_NUMBER)
  );
  private readonly reviewsService = inject(ReviewsService);
  private readonly logs = inject(LogsService);
  private readonly itemsPerPage = signal<number>(0);
  private readonly firstInit = signal(true);

  constructor() {
    effect(() => {
      const isFirstInit = untracked(this.firstInit);
      const itemsToFetch = isFirstInit ? this.currentPageFromUrl() * DEFAULT_ITEMS_PER_PAGE : DEFAULT_ITEMS_PER_PAGE;
      const currentPage = isFirstInit ? 1 : this.currentPageFromUrl();
      const currentRatingFilter = this.selectedRatingFilter();
      this.itemsPerPage.set(itemsToFetch);

      this.reviewsService
        .getListOfReviews({
          pageNumber: currentPage,
          note: currentRatingFilter,
          itemsPerPage: this.itemsPerPage(),
          storeUuid: this.storeUuid(),
        })
        .pipe(
          take(1),
          tap((list) => {
            if (currentPage === 1) {
              this.reviews.set(list.items);
            } else {
              this.reviews.update((reviews) => [...reviews, ...list.items]);
            }
            this.firstInit.set(false);
          })
        )
        .subscribe({ error: (err) => this.logs.error('ReviewListComponent', 'getListOfReviews failed', err) });
    });
  }

  resetRatingFilter(): void {
    this.urlParamsService.removeParams('review_note', 'review_page');
  }

  onReviewViewportEnter($event: boolean, reviewId: number) {
    if (this.loadMoreMode() !== 'scroll') return;
    if ($event) {
      const fifthFromEnd = this.reviews().at(-5);
      if (fifthFromEnd?.id === reviewId) {
        this.loadMore();
      }
    }
  }

  loadMore(): void {
    this.urlParamsService.mergeParams({ review_page: this.currentPageFromUrl() + 1 });
  }
}
